import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  StyleSheet,
  Text,
  View,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  Dimensions,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Data from './Data';
import { useScale, ScaleFn } from './common';

// ─── 테마 (Members.tsx 와 동일 톤) ────────
const C = {
  bg:        '#F9FAFB',
  card:      '#FFFFFF',
  cardInner: '#F3F4F6',
  border:    '#E5E7EB',
  text:      '#111827',
  textSub:   '#4B5563',
  muted:     '#9CA3AF',
  accent:    '#38BDF8',
  ok:        '#34D399',
  warn:      '#FB923C',
  err:       '#F87171',
};

type Person = { IDX: number; NAME: string; AREA: string; ROOM_NO: string };

const pad2 = (n: number) => String(n).padStart(2, '0');
const fmtDate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
const fmtDT = (d: Date) => `${fmtDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
// 현재 시각을 5분 단위(내림)로 — 시간 선택(5분 간격)과 값 일치용
const nowStr = () => { const d = new Date(); d.setMinutes(Math.floor(d.getMinutes() / 5) * 5, 0, 0); return fmtDT(d); };

// react-datepicker 커스텀 인풋 — 폼 스타일에 맞춘 읽기전용 입력(클릭 시 달력+시간 팝업)
const DTInput = React.forwardRef(function DTInput(props: any, ref: any) {
  return (
    <input
      ref={ref}
      onClick={props.onClick}
      value={props.value}
      readOnly
      placeholder="YYYY-MM-DD HH:mm"
      style={{ width: '100%', padding: 10, fontSize: 15, border: '1px solid ' + C.border, borderRadius: 8, backgroundColor: C.card, color: C.text, cursor: 'pointer', boxSizing: 'border-box' }}
    />
  );
});

// 모바일 화면 너비 기반 카드 크기 — 작은 화면 (320~420) 2열, 중간 3열, 큰 화면 4~5열
function useCardWidth() {
  const [w, setW] = useState(Dimensions.get('window').width);
  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => setW(window.width));
    return () => sub.remove();
  }, []);
  const cols = w < 480 ? 2 : w < 720 ? 3 : w < 1024 ? 4 : 5;
  const gap = 10;
  const padding = 24; // 좌우 12 * 2
  return Math.floor((w - padding - gap * (cols - 1)) / cols);
}

export default function Diaper() {
  const isFocused = useIsFocused();
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const cardW = useCardWidth();
  const scrollRef = useRef<ScrollView>(null);
  const [persons, setPersons] = useState<Person[]>([]);
  const [caregivers, setCaregivers] = useState<string[]>([]);
  const [recorderSearch, setRecorderSearch] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);

  const [type, setType] = useState<'소변' | '대변' | ''>('');
  const [subType, setSubType] = useState<string>('');
  const [status, setStatus] = useState<number>(0);
  const [amount, setAmount] = useState<string>('');
  const [recorder, setRecorder] = useState<string>('');
  const [regDate, setRegDate] = useState<string>(nowStr());
  const [toast, setToast] = useState<string>('');
  const [toastErr, setToastErr] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // 이용자 + 기록자 목록 로딩
  useEffect(() => {
    if (!isFocused) return;
    setLoading(true);
    Data.fetchData('diaper/personList', {}, (result: Person[]) => {
      setPersons(result || []);
      setLoading(false);
    }, () => setLoading(false));
    Data.fetchData('diaper/caregiverList', {}, (result: string[]) => {
      setCaregivers(result || []);
    });
  }, [isFocused]);

  // 기록자 검색 필터
  const recorderFiltered = useMemo(() => {
    const q = recorderSearch.trim();
    if (!q) return caregivers;
    return caregivers.filter(n => n.includes(q));
  }, [recorderSearch, caregivers]);

  // 구역 목록 (중복 제거 순서 보존)
  const areas = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    persons.forEach(p => { if (!seen.has(p.AREA)) { seen.add(p.AREA); out.push(p.AREA); }});
    return out;
  }, [persons]);

  // 첫 로딩 시 첫 구역 자동 선택
  useEffect(() => {
    if (areas.length && !selectedArea) setSelectedArea(areas[0]);
  }, [areas]);

  const personsInArea = useMemo(
    () => persons.filter(p => p.AREA === selectedArea),
    [persons, selectedArea]
  );

  const showToast = (msg: string, err: boolean = false) => {
    setToast(msg);
    setToastErr(err);
    setTimeout(() => setToast(''), 2800);
  };

  const resetForm = () => {
    setType(''); setSubType(''); setStatus(0); setAmount(''); setRecorder(''); setRecorderSearch(''); setRegDate(nowStr());
  };

  const submit = () => {
    if (submitting) return;
    if (!selectedPerson) { showToast('이용자를 선택해 주세요', true); return; }
    if (!type)            { showToast('구분(소변/대변)을 선택해 주세요', true); return; }
    const hasFeces = (type === '대변');
    if (hasFeces && !subType) { showToast('대변 종류를 선택해 주세요', true); return; }
    if (!recorder.trim()) { showToast('기록자 이름을 입력해 주세요', true); return; }
    if (!/^[가-힣]{2,5}$/.test(recorder.trim())) {
      showToast('기록자 이름은 한글 2~5자로 입력해 주세요 (예: 홍길동)', true);
      return;
    }

    // 양 — 필수 + 0 < x <= 5000
    if (!amount.trim()) { showToast('양 (g) 을 입력해 주세요', true); return; }
    const n = parseFloat(amount.trim());
    if (isNaN(n) || n <= 0 || n > 5000) {
      showToast('양은 0 초과 5000 이하 숫자로 입력해 주세요', true);
      return;
    }
    const amountNum = String(n);
    // 기록 시각 — 형식 + 미래 거부
    const rd = regDate.trim();
    if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(rd)) { showToast('기록 시각 형식: YYYY-MM-DD HH:mm', true); return; }
    const rdt = new Date(rd.replace(' ', 'T'));
    if (isNaN(rdt.getTime())) { showToast('기록 시각이 올바르지 않습니다', true); return; }
    if (rdt.getTime() > Date.now() + 120000) { showToast('미래 시각은 입력할 수 없습니다', true); return; }
    setSubmitting(true);
    Data.fetchData('diaper/insertStrict', {
      PERSON_IDX: selectedPerson.IDX,
      TYPE: type,
      SUB_TYPE: hasFeces ? subType : null,
      STATUS: status,
      AMOUNT: amountNum,
      RECORDER: recorder.trim(),
      REGDATE: rd + ':00',
    }, (result: any) => {
      setSubmitting(false);
      if (result && result.IDX) {
        showToast(`✓ ${selectedPerson.NAME} ${type}${hasFeces ? '(' + subType + ')' : ''} 기록 완료`);
        resetForm();
        setSelectedPerson(null);
      } else {
        showToast('등록 실패 — 서버 응답 오류', true);
      }
    }, () => { setSubmitting(false); showToast('등록 실패 — 네트워크 오류', true); });
  };

  // 이용자 선택 시 폼이 보이도록 스크롤
  useEffect(() => {
    if (selectedPerson && scrollRef.current) {
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 100);
    }
  }, [selectedPerson]);

  return (
    <KeyboardAvoidingView
      style={[styles.root, { backgroundColor: C.bg }]}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 80 : 0}
    >
      {/* 구역 탭 (페이지 제목은 드로어 헤더에 이미 있음) */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.areaTabs} contentContainerStyle={{ paddingHorizontal: s(12), alignItems: 'center' }}>
        {areas.map(a => (
          <TouchableOpacity
            key={a}
            onPress={() => { setSelectedArea(a); setSelectedPerson(null); resetForm(); }}
            style={[styles.areaTab, selectedArea === a && styles.areaTabActive]}
          >
            <Text style={[styles.areaTabText, selectedArea === a && styles.areaTabTextActive]}>{a}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <ScrollView
        ref={scrollRef}
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: s(12), paddingBottom: s(60) }}
        keyboardShouldPersistTaps="handled"
      >
        {loading && <ActivityIndicator color={C.accent} style={{ marginTop: s(20) }} />}

        {/* 이용자 카드 그리드 — 반응형 cardW */}
        <View style={styles.grid}>
          {personsInArea.map(p => {
            const active = selectedPerson?.IDX === p.IDX;
            return (
              <TouchableOpacity
                key={p.IDX}
                onPress={() => { setSelectedPerson(p); resetForm(); }}
                style={[styles.card, { width: cardW }, active && styles.cardActive]}
                activeOpacity={0.7}
              >
                <Text style={[styles.cardName, active && { color: '#FFF' }]} numberOfLines={1}>{p.NAME}</Text>
                <Text style={[styles.cardRoom, active && { color: '#E0F2FE' }]} numberOfLines={1}>{p.ROOM_NO || '-'}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* 입력 폼 — 이용자 선택 시 노출 */}
        {selectedPerson && (
          <View style={styles.formCard}>
            <View style={styles.formHeader}>
              <Text style={styles.formHeaderText} numberOfLines={1}>
                {selectedPerson.NAME}  <Text style={styles.formHeaderSub}>{selectedPerson.ROOM_NO}</Text>
              </Text>
              <TouchableOpacity
                onPress={() => { setSelectedPerson(null); resetForm(); }}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                style={{ marginLeft: s(12) }}
              >
                <Ionicons name="close" size={s(26)} color={C.textSub} />
              </TouchableOpacity>
            </View>

            {/* 구분 — 큰 chip */}
            <Text style={styles.label}>구분</Text>
            <View style={styles.row}>
              {(['소변', '대변'] as const).map(t => (
                <TouchableOpacity
                  key={t}
                  onPress={() => { setType(t); if (t === '소변') setSubType(''); }}
                  style={[styles.chipBig, type === t && styles.chipActive]}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.chipBigText, type === t && styles.chipTextActive]}>{t}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* 대변 종류 (대변일 때) */}
            {(type === '대변') && (
              <>
                <Text style={styles.label}>대변 종류</Text>
                <View style={styles.row}>
                  {['묽은변', '보통변', '마른변'].map(st => (
                    <TouchableOpacity
                      key={st}
                      onPress={() => setSubType(st)}
                      style={[styles.chipBig, subType === st && styles.chipActive]}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.chipBigText, subType === st && styles.chipTextActive]}>{st}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </>
            )}

            {/* 병증 선택 */}
            <Text style={styles.label}>병증 선택</Text>
            <View style={styles.row}>
              {([
                { v: 0, label: '없음' },
                { v: 1, label: '욕창' },
                { v: 4, label: '요로감염' },
              ] as const).map(opt => (
                <TouchableOpacity
                  key={opt.v}
                  onPress={() => setStatus(opt.v)}
                  style={[styles.chipBig, status === opt.v && styles.chipActive]}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.chipBigText, status === opt.v && styles.chipTextActive]}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* 기록 시각 — 기본 현재. react-datepicker 로 달력+24시간(오전/오후 X) 한 팝업 */}
            <Text style={styles.label}>기록 시각 (날짜·시간 선택, 24시간)</Text>
            <View style={{ flexDirection: 'row', gap: s(8), alignItems: 'center' }}>
              {Platform.OS === 'web' ? (
                <View style={{ flex: 1 }}>
                  {/* 팝업이 폼(ScrollView) 안에서 잘리거나 뒤로 밀리지 않게 portal + 높은 z-index */}
                  <style>{`.react-datepicker-popper{z-index:99999!important}`}</style>
                  <DatePicker
                    selected={regDate ? new Date(regDate.replace(' ', 'T')) : new Date()}
                    onChange={(date: any) => { if (date) setRegDate(fmtDT(date)); }}
                    dateFormat="yyyy-MM-dd HH:mm"
                    timeFormat="HH:mm"
                    showTimeSelect
                    timeIntervals={5}
                    maxDate={new Date()}
                    portalId="diaper-datepicker-portal"
                    popperProps={{ strategy: 'fixed' }}
                    customInput={<DTInput />}
                  />
                </View>
              ) : (
                <TextInput
                  value={regDate}
                  onChangeText={setRegDate}
                  placeholder="YYYY-MM-DD HH:mm"
                  placeholderTextColor={C.muted}
                  style={[styles.input, { flex: 1 }]}
                />
              )}
              <TouchableOpacity onPress={() => setRegDate(nowStr())} style={{ paddingHorizontal: s(12), paddingVertical: s(10), backgroundColor: C.cardInner, borderRadius: s(8), borderWidth: 1, borderColor: C.border }}>
                <Text style={{ color: C.textSub, fontWeight: '600', fontSize: s(13) }}>현재</Text>
              </TouchableOpacity>
            </View>

            {/* 양 — 숫자만 */}
            <Text style={styles.label}>양 (g)</Text>
            <View style={styles.inputUnitWrap}>
              <TextInput
                value={amount}
                onChangeText={(v) => setAmount(v.replace(/[^\d.]/g, ''))}
                placeholder="0"
                placeholderTextColor={C.muted}
                keyboardType="numeric"
                inputMode="decimal"
                style={[styles.input, { flex: 1, paddingRight: s(36) }]}
              />
              <Text style={styles.inputUnit}>g</Text>
            </View>

            {/* 기록자 — 한글 이름 직접 입력 (자모 섞임 / 영문 / 숫자 거부) */}
            <Text style={styles.label}>기록자 이름</Text>
            <TextInput
              value={recorder}
              onChangeText={setRecorder}
              placeholder="예: 홍길동"
              placeholderTextColor={C.muted}
              style={[
                styles.input,
                recorder && !/^[가-힣]{2,5}$/.test(recorder.trim()) && { borderColor: C.err, borderWidth: 1.5 },
              ]}
              maxLength={5}
              returnKeyType="done"
              onSubmitEditing={submit}
            />
            {recorder.trim() && !/^[가-힣]{2,5}$/.test(recorder.trim()) && (
              <Text style={{ color: C.err, fontSize: s(12), marginTop: s(4) }}>
                ✗ 한글 2~5자로 정확히 입력 (자모 단독 ㄱ/ㄴ/ㅏ 불가)
              </Text>
            )}

            <TouchableOpacity
              onPress={submit}
              style={[styles.submit, submitting && { opacity: 0.6 }]}
              activeOpacity={0.8}
              disabled={submitting}
            >
              <Text style={styles.submitText}>{submitting ? '등록 중...' : '등록'}</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>

      {/* 토스트 — 성공/실패 색상 분기 */}
      {!!toast && (
        <View style={[styles.toast, toastErr && { backgroundColor: C.err }]}>
          <Text style={styles.toastText}>{toast}</Text>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
  root: { flex: 1, overflow: 'hidden' },
  areaTabs: { flexGrow: 0, paddingVertical: s(6), borderBottomWidth: 1, borderBottomColor: C.border, backgroundColor: C.card, maxHeight: s(56) },
  areaTab: { paddingHorizontal: s(18), paddingVertical: s(10), borderRadius: s(22), backgroundColor: C.cardInner, marginRight: s(8) },
  areaTabActive: { backgroundColor: C.accent },
  areaTabText: { color: C.textSub, fontWeight: '600', fontSize: s(15) },
  areaTabTextActive: { color: '#FFF' },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: s(10), marginBottom: s(14) },
  card: { padding: s(14), borderRadius: s(12), backgroundColor: C.card, borderWidth: 1, borderColor: C.border, alignItems: 'center', minHeight: s(70), justifyContent: 'center' },
  cardActive: { backgroundColor: C.accent, borderColor: C.accent },
  cardName: { fontSize: s(17), fontWeight: '700', color: C.text },
  cardRoom: { fontSize: s(12), color: C.textSub, marginTop: s(4) },
  formCard: { backgroundColor: C.card, borderRadius: s(14), padding: s(18), marginTop: s(8), borderWidth: 1, borderColor: C.border, shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: s(8), shadowOffset: { width: 0, height: 2 }, elevation: 2 },
  formHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: s(14), paddingBottom: s(12), borderBottomWidth: 1, borderBottomColor: C.border },
  formHeaderText: { fontSize: s(19), fontWeight: '700', color: C.text, flex: 1 },
  formHeaderSub: { fontSize: s(14), color: C.textSub, fontWeight: '400' },
  label: { fontSize: s(14), color: C.textSub, marginTop: s(14), marginBottom: s(8), fontWeight: '600' },
  row: { flexDirection: 'row', gap: s(10) },
  chipBig: { flex: 1, paddingVertical: s(16), borderRadius: s(10), borderWidth: 1, borderColor: C.border, backgroundColor: C.cardInner, alignItems: 'center', minHeight: s(50) },
  chipBigText: { fontSize: s(16), color: C.textSub, fontWeight: '700' },
  chipActive: { backgroundColor: C.accent, borderColor: C.accent },
  chipTextActive: { color: '#FFF' },
  input: { borderWidth: 1, borderColor: C.border, borderRadius: s(10), paddingHorizontal: s(14), paddingVertical: s(14), fontSize: s(17), color: C.text, backgroundColor: C.cardInner, minHeight: s(50) },
  inputUnitWrap: { position: 'relative', flexDirection: 'row', alignItems: 'center' },
  inputUnit: { position: 'absolute', right: s(16), fontSize: s(17), color: C.textSub, fontWeight: '600' },
  submit: { marginTop: s(22), backgroundColor: C.accent, paddingVertical: s(18), borderRadius: s(12), alignItems: 'center', minHeight: s(56) },
  submitText: { color: '#FFF', fontSize: s(18), fontWeight: '700', letterSpacing: 1 },
  recorderGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: s(8) },
  recorderChip: { paddingHorizontal: s(16), paddingVertical: s(12), borderRadius: s(10), borderWidth: 1, borderColor: C.border, backgroundColor: C.cardInner, minHeight: s(44) },
  recorderChipText: { fontSize: s(15), color: C.text, fontWeight: '600' },
  toast: { position: 'absolute', bottom: s(30), left: s(16), right: s(16), backgroundColor: C.ok, padding: s(14), borderRadius: s(12), alignItems: 'center', shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: s(8), shadowOffset: { width: 0, height: 4 }, elevation: 4 },
  toastText: { color: '#FFF', fontSize: s(15), fontWeight: '700' },
});
